import React from 'react'

import { useI18n } from '@runtime/core/i18n/i18n'
import { useUser } from '@runtime/core/user/userContext'

import { Modal } from '@runtime/components/ui/Modal'
import { useAuthFlow } from '@runtime/features/profile/auth/hooks/useAuthFlow'
import { useProfileHistory } from '@runtime/features/profile/history/hooks/useProfileHistory'
import { useProfileActions } from '@runtime/features/profile/account/hooks/useProfileActions'
import ProfileSidebar from '@runtime/features/profile/layout/ProfileSidebar'
import ProfileTab from '@runtime/features/profile/account/tabs/ProfileTab'
import AuthTab from '@runtime/features/profile/auth/tabs/AuthTab'
import RegisterTab from '@runtime/features/profile/auth/tabs/RegisterTab'
import HistoryTab from '@runtime/features/profile/history/tabs/HistoryTab'
import HistoryReview from '@runtime/features/profile/history/review/HistoryReview'
import styles from '@runtime/features/profile/ProfileModal.module.scss'

function ProfileModal({ open }: { open: boolean }) {
  const { t, tNode, locale } = useI18n()
  const {
    user,
    authToken,
    setLoginOpen,
    setAuthToken,
    setUserFromServer,
    updateProfile,
    logout,
    toggleFavoriteGame,
  } = useUser()

  const isAuthed = !!authToken
  const [tab, setTab] = React.useState(isAuthed ? 'profile' : 'login') // login | register | profile | history
  const auth = useAuthFlow({
    open,
    locale,
    t,
    setAuthToken,
    setUserFromServer,
    setLoginOpen,
  })
  const profile = useProfileActions({
    open,
    user,
    authToken,
    t,
    setLoginOpen,
    updateProfile,
    logout,
  })
  const history = useProfileHistory({
    open,
    user,
  })

  React.useEffect(() => {
    if (!open) return

    setTab(isAuthed ? 'profile' : 'login')
  }, [open, isAuthed])

  const rating = user?.rating ?? 1200
  const totalGames = user?.games?.total ?? 0
  const vsAI = user?.games?.vsAI?.played ?? 0
  const puzzlePlayed = user?.games?.puzzle?.played ?? 0
  const favorites = history.favorites
  const initial = '👤'
  const avatarUrl = profile.avatarDataUrl || user?.avatarDataUrl || null

  return (
    <Modal
      open={open}
      onClose={() => setLoginOpen(false)}
      closeOnOverlayClick
      useBaseContentClass={false}
      contentClassName={[
        styles.modalContent,
        tab === 'history' ? styles.modalContentHistory : '',
      ]
        .filter(Boolean)
        .join(' ')}
      contentProps={{ 'data-testid': 'login-panel' }}
    >
      {/* Close button */}
      <button
        type="button"
        className={styles.loginClose}
        data-testid="login-close"
        onClick={() => setLoginOpen(false)}
        aria-label={t('common.close')}
      >
        ×
      </button>

      {/* Main "PLAYER PROFILE" banner */}
      <div className={styles.modalContentHeader}>{t('profile.header')}</div>

      {/* Left column: tabs + data block */}
      <ProfileSidebar
        tab={tab}
        isAuthed={isAuthed}
        setTab={setTab}
        t={t}
        user={user}
        avatarUrl={avatarUrl}
        initial={initial}
        rating={rating}
        totalGames={totalGames}
        vsAI={vsAI}
        puzzlePlayed={puzzlePlayed}
        avatarFileInputRef={profile.avatarFileInputRef}
        onAvatarFileChange={profile.handleAvatarFileChange}
        onAvatarClick={() => profile.handleAvatarClick(isAuthed)}
      />

      {/* Right column: tab content */}
      <div className={styles.loginRight}>
        {tab === 'profile' && isAuthed && (
          <ProfileTab
            t={t}
            user={user}
            exportBusy={profile.exportBusy}
            exportError={profile.exportError}
            deleteBusy={profile.deleteBusy}
            deleteError={profile.deleteError}
            onLogout={profile.handleLogout}
            onSave={profile.handleAvatarSave}
            onExportData={profile.handleExportData}
            onDeleteAccount={profile.handleDeleteAccount}
          />
        )}

        {tab === 'login' && !isAuthed && (
          <AuthTab
            t={t}
            showReset={auth.showReset}
            authError={auth.authError}
            authBusy={auth.authBusy}
            loginEmail={auth.loginEmail}
            loginPassword={auth.loginPassword}
            showLoginPassword={auth.showLoginPassword}
            onLoginEmailChange={auth.setLoginEmail}
            onLoginPasswordChange={auth.setLoginPassword}
            onToggleLoginPassword={auth.toggleLoginPassword}
            onLogin={auth.handleLogin}
            onStartReset={auth.startResetFlow}
            resetStage={auth.resetStage}
            resetEmail={auth.resetEmail}
            resetToken={auth.resetToken}
            resetPassword={auth.resetPassword}
            showResetPassword={auth.showResetPassword}
            resetNotice={auth.resetNotice}
            resetError={auth.resetError}
            resetBusy={auth.resetBusy}
            onResetEmailChange={auth.setResetEmail}
            onResetTokenChange={auth.setResetToken}
            onResetPasswordChange={auth.setResetPassword}
            onToggleResetPassword={auth.toggleResetPassword}
            onResetRequest={auth.handleResetRequest}
            onResetConfirm={auth.handleResetConfirm}
            onExitReset={auth.exitResetFlow}
          />
        )}

        {tab === 'register' && !isAuthed && (
          <RegisterTab
            t={t}
            tNode={tNode}
            registerPseudo={auth.registerPseudo}
            registerEmail={auth.registerEmail}
            registerPassword={auth.registerPassword}
            showRegisterPassword={auth.showRegisterPassword}
            authError={auth.authError}
            authBusy={auth.authBusy}
            onRegisterPseudoChange={auth.setRegisterPseudo}
            onRegisterEmailChange={auth.setRegisterEmail}
            onRegisterPasswordChange={auth.setRegisterPassword}
            onToggleRegisterPassword={auth.toggleRegisterPassword}
            onRegister={auth.handleRegister}
            onCloseModal={() => setLoginOpen(false)}
          />
        )}

        {tab === 'history' && isAuthed && (
          <HistoryTab
            t={t}
            user={user}
            favorites={favorites}
            fullGames={history.fullGames}
            selectedGameId={history.selectedGameId}
            onSelectGame={history.handleSelectGame}
            onToggleFavorite={toggleFavoriteGame}
          />
        )}
      </div>

      {/* Global second row: review block + played moves */}
      {tab === 'history' &&
        history.selectedGame &&
        history.canReviewSelectedGame && (
          <HistoryReview
            t={t}
            selectedGame={history.selectedGame}
            reviewIndex={history.reviewIndex}
            reviewPlaying={history.reviewPlaying}
            setReviewIndex={history.setReviewIndex}
            setReviewPlaying={history.setReviewPlaying}
          />
        )}
    </Modal>
  )
}

export default ProfileModal
